import React, { useContext, createContext, PropsWithChildren, useMemo } from 'react';
import { useImmerReducer } from 'use-immer';

/**
 * Context
 */
export const TableContext = createContext<TableState>({} as TableState);

type TDispatch = {
  dispatch: React.Dispatch<TTableAction>;
};

export const TableDispatchContext = createContext<TDispatch>({} as TDispatch);

interface TableState {
  selectedItems: any[];
}
const initialState: TableState = {
  selectedItems: [],
};

// eslint-disable-next-line react-refresh/only-export-components
export enum TableActionType {
  SELECTING_ITEM = 'SELECTING_ITEM',
  CLEAR_SELECTED_ITEMS = 'CLEAR_SELECTED_ITEMS',
}

export type TTableAction =
  | {
      type: TableActionType.SELECTING_ITEM;
      payload: any[];
    }
  | {
      type: TableActionType.CLEAR_SELECTED_ITEMS;
    };

const tableDispatch = (state: TableState, action: TTableAction) => {
  switch (action.type) {
    case TableActionType.SELECTING_ITEM: {
      state.selectedItems = action.payload;
      return state;
    }
    case TableActionType.CLEAR_SELECTED_ITEMS: {
      return initialState;
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
};

export const TableProvider: React.FC<PropsWithChildren<Partial<TableState>>> = ({ children }) => {
  const [state, dispatch] = useImmerReducer(tableDispatch, initialState);

  const tableData: TableState = useMemo(() => ({ ...state }), [state]);

  const tableDispatchContextProp = useMemo(() => ({ dispatch }), [dispatch]);

  return (
    <TableDispatchContext.Provider value={tableDispatchContextProp}>
      <TableContext.Provider value={tableData}>{children}</TableContext.Provider>
    </TableDispatchContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTableContext = () => useContext(TableContext);
// eslint-disable-next-line react-refresh/only-export-components
export const useTableDispatch = () => useContext(TableDispatchContext);
